/**
 * supabase-crm.ts
 * Handler HTTP delgado: traduce las llamadas de la UI (api.get/post/...) a
 * peticiones a las Edge Functions de Supabase. NO contiene lógica de negocio.
 * Mantiene la firma handleSupabaseApi(method, url, payload, config) para que
 * api.ts y la UI no cambien. Activar con REACT_APP_DATA_MODE=supabase.
 */

import { supabase } from '@/utils/supabase/client';
import { functionsBaseUrl, supabaseKey } from '@/config/runtime';

// ── Helpers de error (misma forma que la capa local) ──────────────────────────

function errorResponse(detail: string, status = 400): never {
  const error: any = new Error(detail);
  error.response = { data: { detail }, status };
  error.status = status;
  throw error;
}

function assertSupabase() {
  if (!supabase) errorResponse('Supabase no está configurado', 500);
  return supabase!;
}

// ── Routing de rutas a funciones ──────────────────────────────────────────────
// Cada ruta de la UI se mapea a una Edge Function por su primer segmento.

function resolveFunction(path: string): { fn: string; rest: string } {
  const clean = path.replace(/^\/+/, '');
  const seg = clean.split('/')[0].split('?')[0];
  if (seg === 'dashboard') return { fn: 'dashboard', rest: clean.slice('dashboard'.length) };
  if (seg === 'reports') return { fn: 'reports', rest: clean.slice('reports'.length) };
  if (seg === 'attachments') return { fn: 'attachments', rest: clean.slice('attachments'.length) };
  // Todo lo demás (leads, contacts, companies, opportunities, activities,
  // search, chile, roles, pipeline-stages, organizations, settings, invitations,
  // auth/register-invite) lo sirve la función crm.
  return { fn: 'crm', rest: '/' + clean };
}

// ── Sesión / headers ──────────────────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const sb = assertSupabase();
  const { data } = await sb.auth.getSession();
  const token = data.session?.access_token || supabaseKey || '';
  const headers: Record<string, string> = {
    apikey: supabaseKey || '',
    Authorization: `Bearer ${token}`,
  };
  if (typeof window !== 'undefined') {
    const orgId = window.localStorage.getItem('currentOrgId');
    if (orgId) headers['x-org-id'] = orgId;
  }
  return headers;
}

function buildUrl(fn: string, rest: string, params?: Record<string, any>): string {
  const base = `${functionsBaseUrl.replace(/\/$/, '')}/${fn}`;
  let url = base + (rest && rest !== '/' ? rest : '');
  if (params && Object.keys(params).length) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const q = qs.toString();
    if (q) url += (url.includes('?') ? '&' : '?') + q;
  }
  return url;
}

async function parseResponse(res: Response, responseType?: string) {
  // CSV / descargas -> blob
  const contentType = res.headers.get('Content-Type') || '';
  if (responseType === 'blob' || contentType.includes('text/csv')) {
    const blob = await res.blob();
    if (!res.ok) errorResponse('Error al descargar', res.status);
    return { data: blob, status: res.status };
  }
  let body: any = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    const detail = (body && body.detail) || `Error ${res.status}`;
    errorResponse(detail, res.status);
  }
  return { data: body, status: res.status };
}

// ── Handler principal ─────────────────────────────────────────────────────────

export async function handleSupabaseApi(
  method: string,
  url: string,
  payload?: any,
  config?: any,
) {
  assertSupabase();
  if (!functionsBaseUrl) errorResponse('Backend de funciones no configurado', 500);

  const { fn, rest } = resolveFunction(url);
  const params = config?.params;
  const target = buildUrl(fn, rest, params);
  const headers = await authHeaders();

  const init: RequestInit = { method, headers };

  // Multipart (upload / import CSV): el payload es FormData -> no fijar Content-Type
  if (payload instanceof FormData) {
    init.body = payload;
  } else if (payload !== undefined && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(payload);
  }

  const res = await fetch(target, init);
  return parseResponse(res, config?.responseType);
}

// ── Auth / onboarding (responsabilidad del cliente, no de lógica de negocio) ───

/**
 * Crea perfil + organización en el primer login (vía RPC setup_new_user) y
 * devuelve el usuario de sesión. Se mantiene aquí porque es parte del flujo Auth.
 */
export async function ensureSupabaseProfile(authUser: any, preferredName?: string) {
  const sb = assertSupabase();
  const name =
    preferredName ||
    authUser.user_metadata?.name ||
    authUser.user_metadata?.full_name ||
    (authUser.email || '').split('@')[0] ||
    'Usuario';

  const { data: existing } = await sb
    .from('profiles')
    .select('id')
    .eq('id', authUser.id)
    .maybeSingle();

  // Usuarios que entran por SSO corporativo (Microsoft 365, dominio @jmkconecta.cl)
  // no crean organización propia: se unen a la organización fija "JMK Conecta".
  // Se detecta por el proveedor de auth (azure/entra) o por el dominio del correo.
  const email = (authUser.email || '').toLowerCase();
  const provider =
    authUser.app_metadata?.provider ||
    (authUser.app_metadata?.providers && authUser.app_metadata.providers[0]);
  const isSsoUser =
    provider === 'azure' ||
    provider === 'entra' ||
    email.endsWith('@jmkconecta.cl');

  if (!existing) {
    if (isSsoUser) {
      const { data: setupData, error } = await sb.rpc('setup_sso_user', {
        p_name: name,
        p_email: authUser.email,
      });
      if (error) errorResponse(error.message, 500);
      const orgId = (setupData as any)?.org_id;
      if (orgId && typeof window !== 'undefined') {
        window.localStorage.setItem('currentOrgId', orgId);
      }
    } else {
      const { data: setupData, error } = await sb.rpc('setup_new_user', {
        p_name: name,
        p_email: authUser.email,
        p_org_name: `Empresa de ${name}`,
      });
      if (error) errorResponse(error.message, 500);
      const orgId = (setupData as any)?.org_id;
      if (orgId && typeof window !== 'undefined') {
        window.localStorage.setItem('currentOrgId', orgId);
      }
    }
  } else if (typeof window !== 'undefined' && !window.localStorage.getItem('currentOrgId')) {
    // Si ya tiene perfil, resolver su org por defecto
    const { data: m } = await sb.from('memberships').select('org_id').eq('user_id', authUser.id).limit(1).maybeSingle();
    if (m?.org_id) window.localStorage.setItem('currentOrgId', m.org_id as string);
  }

  return getSupabaseSessionUser(authUser.id);
}

/** Arma el usuario de sesión (perfil + membresías + rol + permisos) desde Supabase. */
export async function getSupabaseSessionUser(userId: string) {
  const sb = assertSupabase();

  const { data: profile } = await sb.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (!profile) return null;

  const { data: memberships } = await sb
    .from('memberships')
    .select('org_id, role, organizations(name)')
    .eq('user_id', userId);

  const mapped = (memberships || []).map((m: any) => ({
    org_id: m.org_id,
    org_name: m.organizations?.name || 'Organizacion',
    role: m.role,
  }));

  let currentOrgId = typeof window !== 'undefined' ? window.localStorage.getItem('currentOrgId') : null;
  if (!currentOrgId && mapped[0]) {
    currentOrgId = mapped[0].org_id;
    if (typeof window !== 'undefined' && currentOrgId) window.localStorage.setItem('currentOrgId', currentOrgId);
  }
  const current = mapped.find((m) => m.org_id === currentOrgId) || mapped[0];

  // Permisos del rol actual (desde la tabla roles de la org)
  let permissions: any = {};
  if (current) {
    const { data: roleRow } = await sb
      .from('roles')
      .select('permissions')
      .eq('tenant_id', current.org_id)
      .eq('key', current.role)
      .maybeSingle();
    permissions = roleRow?.permissions || {};
  }

  return {
    id: (profile as any).id,
    name: (profile as any).name,
    email: (profile as any).email,
    role: current?.role || 'viewer',
    memberships: mapped,
    permissions,
  };
}

/** Acepta una invitación (RPC accept_invitation) y devuelve el usuario de sesión. */
export async function acceptSupabaseInvitation(token: string, authUser: any, preferredName?: string) {
  const sb = assertSupabase();
  // Asegurar perfil del usuario (sin crear org nueva si la invitación define una)
  const name = preferredName || authUser.user_metadata?.name || (authUser.email || '').split('@')[0] || 'Usuario';
  await sb.from('profiles').upsert({ id: authUser.id, name, email: authUser.email }, { onConflict: 'id' });

  const { error } = await sb.rpc('accept_invitation', { p_token: token, p_user_id: authUser.id });
  if (error) errorResponse(error.message, 400);

  return getSupabaseSessionUser(authUser.id);
}
